import { getCurrentChainId, getServerApi } from '@/gql';
import { UserVo } from '@/gql/graph/__generated__/request';
import { makeAutoObservable, reaction } from 'mobx';
import { ChainId, DefaultChainId } from '../constant';

export class User {
    address: string = '';
    isDark: boolean = false;
    chainId: ChainId = getCurrentChainId();
    connected: boolean = false;
    userId: string | null = null;
    isLoadingUserDetail: boolean = false;
    userDetail: UserVo | null = null;
    referrerCode: string = ''; // Referrer's invite code that invited current user
    private disposer: (() => void) | null = null;

    constructor() {
        makeAutoObservable<this>(this);
        this.setupReactions();
    }

    private setupReactions() {
        this.disposer = reaction(
            () => this.userId,
            async (userId) => {
                if (userId) {
                    await this.fetchUserDetail();
                }
            },
            { fireImmediately: true },
        );
    }

    fetchUserDetail = async () => {
        if (!this.userId) return;

        try {
            this.isLoadingUserDetail = true;
            const api = getServerApi();
            const response = await api.getUserDetail({ id: this.userId });
            if (
                response?.getUserDetail?.data &&
                response?.getUserDetail?.success
            ) {
                this.userDetail = response.getUserDetail.data;
                return this.userDetail;
            }
        } catch (error) {
            console.error('Failed to fetch user detail:', error);
            return null;
        } finally {
            this.isLoadingUserDetail = false;
        }
    };

    toggleTheme = () => {
        this.isDark = !this.isDark;
    };

    setConnected = (connected: boolean) => {
        this.connected = connected;
    };

    setChainId = (chainId: ChainId) => {
        this.chainId = chainId;
    };

    setAddress = (address: string) => {
        this.address = address;
    };

    setUserId = (userId: string | null) => {
        this.userId = userId;
    };

    setUserDetail = (userDetail: UserVo) => {
        this.userDetail = userDetail;
    };

    setReferrerCode = (referrerCode: string) => {
        this.referrerCode = referrerCode;
    };

    clearLoginInfo = () => {
        this.chainId = DefaultChainId;
        this.address = '';
        this.connected = false;
        this.userDetail = null;
        this.userId = null;
        this.isLoadingUserDetail = false;
    };

    dispose = () => {
        if (this.disposer) {
            this.disposer();
            this.disposer = null;
        }
    };
}

export default new User();
